import { createHostedMarketplaceConfigAsset } from './hostedConfigAdapter';
import { normalizeMarketplaceConfig } from './normalizeConfig';
import { createStaticMarketplaceConfigAsset } from './staticConfig';

const isTrue = value => String(value || '').toLowerCase() === 'true';

// Toggle for merging hosted config with local static fallback when hosted config is enabled.
// Defaults to true to keep the resilient behavior. Set REACT_APP_ENABLE_STATIC_FALLBACK=false
// to disable fallback merging and rely on hosted assets only.
const shouldMergeStaticFallback = () => {
  const envValue = process.env.REACT_APP_ENABLE_STATIC_FALLBACK;
  return envValue == null ? true : isTrue(envValue);
};

const normalizeEnumOption = option => {
  if (option == null) {
    return null;
  }

  if (typeof option === 'string' || typeof option === 'number') {
    const value = `${option}`.trim();
    return value ? { option: value, label: value } : null;
  }

  if (typeof option !== 'object') {
    return null;
  }

  const rawOption =
    option.option ?? option.key ?? option.id ?? option.values ?? option.value ?? null;
  const rawLabel = option.label ?? option.name ?? rawOption;
  const normalizedOption = rawOption != null ? `${rawOption}`.trim() : '';
  const normalizedLabel = rawLabel != null ? `${rawLabel}`.trim() : '';

  return normalizedOption && normalizedLabel
    ? { ...option, option: normalizedOption, label: normalizedLabel }
    : null;
};

const uniqueOptionKey = option => {
  const normalizedOption = normalizeEnumOption(option);
  return normalizedOption?.option || null;
};

const mergeEnumOptions = (primaryOptions = [], fallbackOptions = []) => {
  const merged = [];
  const seen = new Set();

  const add = rawOption => {
    const option = normalizeEnumOption(rawOption);
    const key = uniqueOptionKey(option);
    if (!key || seen.has(key)) return;
    seen.add(key);
    merged.push(option);
  };

  primaryOptions.forEach(add);
  fallbackOptions.forEach(add);

  return merged;
};

const normalizeListingFields = listingFields => {
  return (listingFields || []).map(field => {
    const enumOptions = Array.isArray(field?.enumOptions)
      ? mergeEnumOptions(field.enumOptions, [])
      : field?.enumOptions;

    return enumOptions ? { ...field, enumOptions } : field;
  });
};

const normalizeConfigAssetShape = configAsset => {
  const listingFields = configAsset?.listingFields?.listingFields || [];
  return {
    ...configAsset,
    listingFields: { listingFields: normalizeListingFields(listingFields) },
  };
};

const mergeListingField = (primaryField, fallbackField) => {
  if (!primaryField && !fallbackField) return null;
  if (!fallbackField) return primaryField;
  if (!primaryField) return fallbackField;

  const mergedEnumOptions = mergeEnumOptions(
    Array.isArray(primaryField.enumOptions) ? primaryField.enumOptions : [],
    Array.isArray(fallbackField.enumOptions) ? fallbackField.enumOptions : []
  );

  return {
    ...fallbackField,
    ...primaryField,
    enumOptions: mergedEnumOptions,
    categoryConfig: primaryField.categoryConfig || fallbackField.categoryConfig,
    filterConfig: { ...(fallbackField.filterConfig || {}), ...(primaryField.filterConfig || {}) },
    showConfig: { ...(fallbackField.showConfig || {}), ...(primaryField.showConfig || {}) },
    saveConfig: { ...(fallbackField.saveConfig || {}), ...(primaryField.saveConfig || {}) },
  };
};

const mergeListingFieldsWithFallback = (primaryFields = [], fallbackFields = []) => {
  const fallbackByKey = fallbackFields.reduce((acc, field) => {
    const key = field?.key;
    return key ? { ...acc, [key]: field } : acc;
  }, {});

  const seenKeys = new Set();

  const mergedPrimary = primaryFields
    .map(field => {
      const key = field?.key;
      if (key) {
        seenKeys.add(key);
      }

      return mergeListingField(field, key ? fallbackByKey[key] : null);
    })
    .filter(Boolean);

  const missingFallbackFields = fallbackFields.filter(field => {
    const key = field?.key;
    return key && !seenKeys.has(key);
  });

  return [...mergedPrimary, ...missingFallbackFields];
};

const mergeListingTypesWithFallback = (primaryTypes = [], fallbackTypes = []) => {
  const seenIds = new Set();
  const merged = [];

  primaryTypes.forEach(type => {
    const id = type?.id;
    if (id) {
      seenIds.add(id);
    }
    merged.push(type);
  });

  fallbackTypes.forEach(type => {
    const id = type?.id;
    if (id && !seenIds.has(id)) {
      merged.push(type);
    }
  });

  return merged;
};

const mergeHostedWithStaticFallback = (hostedConfigAsset, staticConfigAsset) => {
  const hostedListingFields = hostedConfigAsset?.listingFields?.listingFields || [];
  const staticListingFields = staticConfigAsset?.listingFields?.listingFields || [];
  const mergedListingFields = mergeListingFieldsWithFallback(hostedListingFields, staticListingFields);

  const hostedListingTypes = hostedConfigAsset?.listingTypes?.listingTypes || [];
  const staticListingTypes = staticConfigAsset?.listingTypes?.listingTypes || [];
  const mergedListingTypes = mergeListingTypesWithFallback(hostedListingTypes, staticListingTypes);

  const hasHostedCategories = hostedConfigAsset?.categories?.categories?.length > 0;
  const hasHostedSearchConfig = hostedConfigAsset?.search &&
    Object.keys(hostedConfigAsset.search).length > 0;

  return {
    ...staticConfigAsset,
    ...hostedConfigAsset,
    categories: hasHostedCategories ? hostedConfigAsset.categories : staticConfigAsset.categories,
    listingTypes: { listingTypes: mergedListingTypes },
    listingFields: { listingFields: mergedListingFields },
    search: hasHostedSearchConfig ? hostedConfigAsset.search : staticConfigAsset.search,
  };
};

export const shouldUseHostedMarketplaceConfig = () => {
  return isTrue(process.env.REACT_APP_USE_HOSTED_CONFIG);
};

const findFieldOptions = (fieldName, normalizedConfig) => {
  const key = fieldName != null ? `${fieldName}`.trim() : '';
  return key ? normalizedConfig?.listingFieldOptions?.[key] || [] : [];
};

export const getMarketplaceConfig = ({ hostedConfig = {} } = {}) => {
  const useHostedConfig = shouldUseHostedMarketplaceConfig();
  const mergeWithFallback = shouldMergeStaticFallback();
  const hostedConfigAsset = createHostedMarketplaceConfigAsset(hostedConfig);
  const staticConfigAsset = createStaticMarketplaceConfigAsset();

  const configAssetForMerge = useHostedConfig
    ? mergeWithFallback
      ? mergeHostedWithStaticFallback(hostedConfigAsset, staticConfigAsset)
      : hostedConfigAsset
    : {
        ...hostedConfigAsset,
        categories: staticConfigAsset.categories,
        listingTypes: staticConfigAsset.listingTypes,
        listingFields: staticConfigAsset.listingFields,
        search: staticConfigAsset.search,
      };

  const normalizedConfigAssetForMerge = normalizeConfigAssetShape(configAssetForMerge);
  const normalizedConfig = normalizeMarketplaceConfig(normalizedConfigAssetForMerge);

  if (useHostedConfig && process.env.NODE_ENV === 'development') {
    const emptyEnumFields = (normalizedConfig?.listingFields || []).filter(field => {
      const isEnumField = ['enum', 'multi-enum'].includes(field?.schemaType);
      const hasOptions = Array.isArray(field?.enumOptions) && field.enumOptions.length > 0;
      return isEnumField && !hasOptions;
    });

    if (emptyEnumFields.length > 0) {
      const missingKeys = emptyEnumFields.map(f => f.key).join(', ');
      // eslint-disable-next-line no-console
      console.warn(
        '[config] Hosted config is missing enum options. Falling back to static options where possible.',
        { missingKeys }
      );
    }
  }

  return {
    source: useHostedConfig ? 'hosted' : 'static',
    useHostedConfig,
    getConfigAssetForMerge: () => normalizedConfigAssetForMerge,
    getFilterOptions: () => normalizedConfig.filterOptions,
    getCategoryTree: () => normalizedConfig.categories,
    getListingFieldOptions: fieldName => findFieldOptions(fieldName, normalizedConfig),
    getSearchFilterDefinitions: () => normalizedConfig.search,
  };
};

export const getFilterOptions = args => getMarketplaceConfig(args).getFilterOptions();

export const getCategoryTree = args => getMarketplaceConfig(args).getCategoryTree();

export const getListingFieldOptions = (fieldName, args = {}) => {
  const configFields = args?.config?.listing?.listingFields || [];
  if (Array.isArray(configFields) && configFields.length > 0) {
    const foundField = configFields.find(field => field?.key === fieldName);
    if (Array.isArray(foundField?.enumOptions) && foundField.enumOptions.length > 0) {
      return foundField.enumOptions;
    }
  }

  return getMarketplaceConfig(args).getListingFieldOptions(fieldName);
};

export const getSearchFilterDefinitions = args =>
  getMarketplaceConfig(args).getSearchFilterDefinitions();
